import { Defs, Document, G, Image, Page, Path, Svg, Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { enGB } from "date-fns/locale";
import { ReactNode } from 'react';
import { cloudsLabel, degreesToDirection, formatDelay, formatFlightTime, getDelayColor } from '../../../lib/types';
import { ReportDTO } from '../../../pages/report/dto';
import { colors } from '../../color';

import { Font } from '@react-pdf/renderer';
import { formatInTimeZone } from 'date-fns-tz';
import { PillsPdf } from './pills';
import { CRITICAL_NOTAM_RULES, htmlToPdfElements } from './types';

Font.register({
    family: 'Inter',
    fonts: [
        { src: '/fonts/Inter_18pt-Regular.ttf', fontWeight: 400 },
        { src: '/fonts/Inter_18pt-Semibold.ttf', fontWeight: 400 },
        { src: '/fonts/Inter_18pt-Bold.ttf', fontWeight: 700 },
        { src: '/fonts/Inter_18pt-ExtraBold.ttf', fontWeight: 800 },
    ],
});

const Footer = ({ sources }: { sources: string }) => (
    <View
        fixed
        style={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            right: 20,
            height: 24,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 8,
            color: colors.pas[800],
            fontFamily: 'Inter',
            fontWeight: 400,
        }}
    >
        <Text>{sources}</Text>
        <Text render={({ pageNumber, totalPages }) => `Pagina ${String(pageNumber).padStart(2, '0')} di ${String(totalPages).padStart(2, '0')}`} />
    </View>
);

interface Props {
    report: ReportDTO
}

export function PDF(props: Props) {
    let now = new Date()
    const tenDaysFromNow = new Date();
    tenDaysFromNow.setDate(now.getDate() + 10);

    let sourcesApiArrivals: string = ""
    let sourcesApiDepartures: string = ""
    let sourcesApi: string = ""
    let sourcesApiMetarDeparture: string = ""
    let sourcesApiMetarArrivals: string = ""
    let sourcesApiNotam: string = ""

    if (props.report.flight_info.flight.departure.flight_gate_times.scheduled_out) {
        let date = new Date(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out)
        sourcesApiMetarDeparture = "https://ogimet.com/display_metars2.php?lang=en&lugar=" + props.report.flight_info.flight.departure.airport.code_icao + "&tipo=ALL&ord=REV&nil=SI&fmt=txt&ano=" + date.getFullYear() + "&mes=" + (date.getMonth() + 1) + "&day=" + date.getDate() + "&hora=0&min=0&ndays=1"
    }

    if (props.report.flight_info.flight.arrival.flight_gate_times.scheduled_in) {
        let date = new Date(props.report.flight_info.flight.arrival.flight_gate_times.scheduled_in)
        sourcesApiMetarArrivals = "https://ogimet.com/display_metars2.php?lang=en&lugar=" + props.report.flight_info.flight.arrival.airport.code_icao + "&tipo=ALL&ord=REV&nil=SI&fmt=txt&ano=" + date.getFullYear() + "&mes=" + (date.getMonth() + 1) + "&day=" + date.getDate() + "&hora=0&min=0&ndays=1"
    }

    if (format(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out, "dd-MM-yyyy") >= format(now, "dd-MM-yyyy") && format(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out, "dd-MM-yyyy") <= format(tenDaysFromNow, "dd-MM-yyyy")) {
        sourcesApi = "Fonte: FlightAware API - flights/" + props.report.flight_info.flight.ident_iata
        sourcesApiArrivals = "Fonte: FlightAware API - airports/" + props.report.flight_info.flight.arrival.airport.code_iata + "/flights/arrivals"
        sourcesApiDepartures = "Fonte: FlightAware API - airports/" + props.report.flight_info.flight.departure.airport.code_iata + "/flights/departures"
    } else {
        sourcesApi = "Fonte: FlightAware API - history/flights/" + props.report.flight_info.flight.ident_iata
        sourcesApiArrivals = "Fonte: FlightAware API - history/airports/" + props.report.flight_info.flight.arrival.airport.code_iata + "/flights/arrivals"
        sourcesApiDepartures = "Fonte: FlightAware API -  history/airports/" + props.report.flight_info.flight.departure.airport.code_iata + "/flights/departures"
    }

    if (format(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out, "dd-MM-yyyy") === format(new Date(), "dd-MM-yyyy")) {
        sourcesApiNotam = "Fonte: FlightAware API - https://api.notamify.com/api/get-notams"
    } else {
        sourcesApiNotam = "Fonte: FlightAware API - https://api.notamify.com/api/v2/notams/archive"
    }

    const chunkArray = (arr: any, size: number) => {
        return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size)
        );
    };

    const filterCriticalNotams = (notamList: any[]) => {
        return notamList.reduce((acc, notam) => {
            const textToAnalyze = (notam.message + " " + notam.description).toUpperCase();
            const matchedRule = CRITICAL_NOTAM_RULES.find(rule => rule.regex.test(textToAnalyze));

            if (matchedRule) {
                acc.push({
                    ...notam,
                    ai_main_category: matchedRule.category
                });
            }
            return acc;
        }, []);
    };

    interface TableHeaderCellProps {
        children: React.ReactNode;
        width: number | string;
    }

    const TableHeaderCell: React.FC<TableHeaderCellProps> = ({ children, width }) => (
        <View style={{
            width,
            paddingVertical: 16,
            paddingHorizontal: 8,
            backgroundColor: colors.neutral[100],
            justifyContent: "flex-start",
            alignItems: "flex-start",
        }}>
            <Text style={{ fontSize: "7px", fontWeight: 600, color: colors.neutral[500], fontFamily: 'Inter' }}>{children}</Text>
        </View>
    );

    const TableCell: React.FC<{ children: React.ReactNode; width: number | string; }> = ({ children, width }) => (
        <View style={{
            width,
            paddingVertical: 6,
            paddingHorizontal: 8,
            borderBottomWidth: 1,
            borderBottomColor: colors.neutral[100],
            display: "flex",
            flexDirection: "column",
            gap: 2,
            justifyContent: "flex-start"
        }}>
            {children}
        </View>
    );

    const rotations = props.report.flight_info.flight.aircraft_rotation
    const departures = props.report.flight_info.departures
    const arrivals = props.report.flight_info.arrivals
    const departureWeather = props.report.flight_info.departure_airport_weather.hourly
    const arrivalWeather = props.report.flight_info.arrival_airport_weather.hourly
    const rawDepartureNotams = props.report.flight_info.departure_airport_notams ?? [];
    const rawArrivalNotams = props.report.flight_info.arrival_airport_notams ?? [];
    const departureNotams = filterCriticalNotams(rawDepartureNotams);
    const arrivalNotams = filterCriticalNotams(rawArrivalNotams);
    const notamDepartureChunks = chunkArray(departureNotams, 9);
    const notamArrivalChunks = chunkArray(arrivalNotams, 9);

    let rotationBody: ReactNode[] = []
    let departuresBody: ReactNode[] = []
    let arrivalsBody: ReactNode[] = []
    let departuresWeatherBody: ReactNode[] = []
    let arrivalsWeatherBody: ReactNode[] = []

    if (props.report.flight_info.flight.aircraft_rotation !== null) {
        props.report.flight_info.flight.aircraft_rotation.filter(flight => format(flight.departure.flight_gate_times.scheduled_out, "yyyy-MM-dd") !== "0001-01-01").forEach((item, index) => {
            const departureDelayColor = getDelayColor(item.departure.flight_gate_times.departure_delay)
            const arrivalDelayColor = getDelayColor(item.arrival.flight_gate_times.arrival_delay)

            rotationBody.push(
                <View key={index} style={{ flexDirection: "row" }}>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {new Date(item.departure.flight_gate_times.scheduled_out).toISOString() === "0001-01-01T00:00:56.000Z"
                                ? "-"
                                : formatInTimeZone(item.departure.flight_gate_times.scheduled_out_tz, item.departure.airport.timezone, "dd MMM yyyy", { locale: enGB })}
                        </Text>
                        <PillsPdf
                            label={item.history.diverted ? "Diverted" : item.history.cancelled ? "Canceled" : item.history.blocked ? "Blocked" : "Landed"}
                            color={(item.history.diverted || item.history.cancelled) ? "red" : item.history.blocked ? "orange" : "green"}
                            size={'sm'}
                            outline={false}
                            emphasis={false}
                        />
                    </TableCell>
                    <TableCell width="100%">
                        <View style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <View style={{ display: "flex", flexDirection: "row", gap: 4, alignItems: "center" }}>
                                <Text style={{ fontSize: "6px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                    {item.ident_iata}
                                </Text>
                                <Text style={{ fontSize: "5px", fontWeight: 600, color: colors.neutral[600], fontFamily: 'Inter' }}>
                                    {item.ident}
                                </Text>
                            </View>
                            <Text style={{ fontSize: "5px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                {item.operator + " " + item.airline_name}
                            </Text>
                            <Text style={{ fontWeight: 600, fontSize: "6px", color: colors.neutral[600], fontFamily: 'Inter' }}>
                                {item.operator + "/" + item.operator_iata}
                            </Text>
                        </View>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.departure.airport.city + " (" + item.departure.airport.code_iata + ")"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.arrival.airport.city + " (" + item.arrival.airport.code_iata + ")"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.departure.flight_gate_times.scheduled_out, "yyyy-MM-dd") === "0001-01-01"
                                ? "-"
                                : formatInTimeZone(item.departure.flight_gate_times.scheduled_out_tz, item.departure.airport.timezone, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.departure.flight_gate_times.scheduled_out, "yyyy-MM-dd") === "0001-01-01"
                                ? ""
                                : formatInTimeZone(item.departure.flight_gate_times.scheduled_out_tz, item.departure.airport.timezone, "HH:mm", { locale: enGB }) +
                                " " +
                                item.departure.airport.timezone_abbr
                            }
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.departure.flight_gate_times.actual_out, "yyyy-MM-dd") === "0001-01-01")
                                    ? "-"
                                    : formatInTimeZone(item.departure.flight_gate_times.actual_out_tz, item.departure.airport.timezone, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.departure.flight_gate_times.actual_out, "yyyy-MM-dd") === "0001-01-01")
                                    ? ""
                                    : formatInTimeZone(item.departure.flight_gate_times.actual_out_tz, item.departure.airport.timezone, "HH:mm", { locale: enGB }) + " " + item.departure.airport.timezone_abbr
                            }
                        </Text>
                        {
                            formatDelay(item.departure.flight_gate_times.departure_delay) && format(item.departure.flight_gate_times.actual_out, "yyyy-MM-dd") !== "0001-01-01" ?
                                <PillsPdf
                                    label={formatDelay(item.departure.flight_gate_times.departure_delay)!}
                                    color={departureDelayColor}
                                    size={'sm'} outline={false} emphasis={false} />
                                : format(item.departure.flight_gate_times.actual_out, "yyyy-MM-dd") !== "0001-01-01" ?
                                    <PillsPdf
                                        label={"On time"}
                                        color={"green"}
                                        size={'sm'} outline={false} emphasis={false} />
                                    : <></>
                        }
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.arrival.flight_gate_times.scheduled_in, "yyyy-MM-dd") === "0001-01-01"
                                ? "-"
                                : formatInTimeZone(item.arrival.flight_gate_times.scheduled_in_tz, item.arrival.airport.timezone, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.arrival.flight_gate_times.scheduled_in, "yyyy-MM-dd") === "0001-01-01"
                                ? ""
                                : formatInTimeZone(item.arrival.flight_gate_times.scheduled_in_tz, item.arrival.airport.timezone, "HH:mm", { locale: enGB }) +
                                " " +
                                item.arrival.airport.timezone_abbr
                            }
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.arrival.flight_gate_times.actual_in_tz, "yyyy-MM-dd") === "0001-01-01") ? "-" :
                                    formatInTimeZone(item.arrival.flight_gate_times.actual_in_tz, item.arrival.airport.timezone, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.arrival.flight_gate_times.actual_in_tz, "yyyy-MM-dd") === "0001-01-01") ? "" :
                                    formatInTimeZone(item.arrival.flight_gate_times.actual_in_tz, item.arrival.airport.timezone, "HH:mm", { locale: enGB }) +
                                    " " +
                                    item.arrival.airport.timezone_abbr
                            }
                        </Text>
                        {
                            formatDelay(item.arrival.flight_gate_times.arrival_delay) && format(item.arrival.flight_gate_times.actual_in, "yyyy-MM-dd") !== "0001-01-01" ?
                                <PillsPdf
                                    label={formatDelay(item.arrival.flight_gate_times.arrival_delay)!}
                                    color={arrivalDelayColor}
                                    size={'sm'} outline={false} emphasis={false} />
                                : format(item.arrival.flight_gate_times.actual_in, "yyyy-MM-dd") !== "0001-01-01" ?
                                    <PillsPdf
                                        label={"On time"}
                                        color={"green"}
                                        size={'sm'} outline={false} emphasis={false} />
                                    : <></>
                        }
                    </TableCell>
                </View>
            )
        })
    }

    const rotationChunks = chunkArray(rotationBody, 13);

    if (props.report.flight_info.departures !== null) {
        props.report.flight_info.departures.filter(flight => flight.flight_number !== "" && format(new Date(flight.scheduled_out_tz), "yyyy-MM-dd") !== "0001-01-01").forEach((item, index) => {
            const departuresDelayColor = getDelayColor(item.departure_delay)
            const arrivalsDelayColor = getDelayColor(item.arrival_delay)

            departuresBody.push(
                <View key={index} style={{ flexDirection: "row" }}>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {new Date(item.scheduled_out).toISOString() === "0001-01-01T00:00:56.000Z"
                                ? "-"
                                : formatInTimeZone(item.scheduled_out_tz, item.from_tz, "dd MMM yyyy HH:mm", { locale: enGB })}
                        </Text>
                        <PillsPdf
                            label={item.status.diverted ? "Diverted" : item.status.cancelled ? "Canceled" : item.status.blocked ? "Blocked" : "Landed"}
                            color={(item.status.diverted || item.status.cancelled) ? "red" : item.status.blocked ? "orange" : "green"}
                            size={'sm'}
                            outline={false}
                            emphasis={false}
                        />
                    </TableCell>
                    <TableCell width="100%">
                        <View style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <View style={{ display: "flex", flexDirection: "row", gap: 4, alignItems: "center" }}>
                                <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                    {item.ident_iata}
                                </Text>
                                <Text style={{ fontSize: "6px", fontWeight: 600, color: colors.neutral[600], fontFamily: 'Inter' }}>
                                    {item.ident}
                                </Text>
                            </View>
                            <Text style={{ fontSize: "6px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                {item.operator + " " + item.airline_name}
                            </Text>
                            <Text style={{ fontWeight: 600, fontSize: "8px", color: colors.neutral[600], fontFamily: 'Inter' }}>
                                {item.operator + "/" + item.operator_iata}
                            </Text>
                        </View>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[800], fontWeight: 600, fontFamily: 'Inter' }}>
                            {item.to.city + " (" + item.to.code_iata + "/" + item.to.code_icao + ")"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.scheduled_out, "yyyy-MM-dd") === "0001-01-01"
                                ? "-"
                                : formatInTimeZone(item.scheduled_out_tz, item.from_tz, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.scheduled_out, "yyyy-MM-dd") === "0001-01-01"
                                ? ""
                                : formatInTimeZone(item.scheduled_out_tz, item.from_tz, "HH:mm", { locale: enGB }) +
                                " " +
                                item.from_tz_abbr
                            }
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.actual_out, "yyyy-MM-dd") === "0001-01-01")
                                    ? "-"
                                    : formatInTimeZone(item.actual_out_tz, item.from_tz, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{
                            fontSize: "7px", color: colors.neutral[600],
                            fontFamily: 'Inter', fontWeight: 600
                        }}>
                            {
                                (format(item.actual_out, "yyyy-MM-dd") === "0001-01-01")
                                    ? ""
                                    : formatInTimeZone(item.actual_out_tz, item.from_tz, "HH:mm", { locale: enGB }) + " " + item.from_tz_abbr
                            }
                        </Text>
                        {
                            formatDelay(item.departure_delay) && format(item.actual_out, "yyyy-MM-dd") !== "0001-01-01" ?
                                <PillsPdf
                                    label={formatDelay(item.departure_delay)!}
                                    color={departuresDelayColor}
                                    size={'sm'} outline={false} emphasis={false} />
                                : format(item.actual_out, "yyyy-MM-dd") !== "0001-01-01" ?
                                    <PillsPdf
                                        label={"On time"}
                                        color={"green"}
                                        size={'sm'} outline={false} emphasis={false} /> :
                                    <></>
                        }
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.actual_in, "yyyy-MM-dd") === "0001-01-01") ? "-" :
                                    formatInTimeZone(item.actual_in_tz, item.to.timezone, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.actual_in, "yyyy-MM-dd") === "0001-01-01") ? "" :
                                    formatInTimeZone(item.actual_in_tz, item.to.timezone, "HH:mm", { locale: enGB }) +
                                    " " +
                                    item.to.timezone_abbr
                            }
                        </Text>
                        {
                            formatDelay(item.arrival_delay) && format(item.actual_in, "yyyy-MM-dd") !== "0001-01-01" ?
                                <PillsPdf
                                    label={formatDelay(item.arrival_delay)!}
                                    color={arrivalsDelayColor}
                                    size={'sm'} outline={false} emphasis={false} />
                                : format(item.actual_in, "yyyy-MM-dd") !== "0001-01-01" ?
                                    <PillsPdf
                                        label={"On time"}
                                        color={"green"}
                                        size={'sm'} outline={false} emphasis={false} /> : <></>
                        }
                    </TableCell>
                </View>
            )
        })
    }

    const departuresAirportChunks = chunkArray(departuresBody, 13);

    if (props.report.flight_info.arrivals !== null) {
        props.report.flight_info.arrivals.filter(flight => flight.flight_number !== "" && format(new Date(flight.scheduled_in_tz), "yyyy-MM-dd") !== "0001-01-01").forEach((item, index) => {
            const departuresDelayColor = getDelayColor(item.departure_delay)
            const arrivalsDelayColor = getDelayColor(item.arrival_delay)

            arrivalsBody.push(
                <View key={index} style={{ flexDirection: "row" }}>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {new Date(item.scheduled_in).toISOString() === "0001-01-01T00:00:56.000Z"
                                ? "-"
                                : formatInTimeZone(item.scheduled_in_tz, item.to_tz, "dd MMM yyyy HH:mm", { locale: enGB })
                            }
                        </Text>
                        <PillsPdf
                            label={item.status.diverted ? "Diverted" : item.status.cancelled ? "Canceled" : item.status.blocked ? "Blocked" : "Landed"}
                            color={(item.status.diverted || item.status.cancelled) ? "red" : item.status.blocked ? "orange" : "green"}
                            size={'sm'}
                            outline={false}
                            emphasis={false}
                        />
                    </TableCell>
                    <TableCell width="100%">
                        <View style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <View style={{ display: "flex", flexDirection: "row", gap: 4, alignItems: "center" }}>
                                <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                    {item.ident_iata}
                                </Text>
                                <Text style={{ fontSize: "6px", fontWeight: 600, color: colors.neutral[600], fontFamily: 'Inter' }}>
                                    {item.ident}
                                </Text>
                            </View>
                            <Text style={{ fontSize: "6px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                {item.operator + " " + item.airline_name}
                            </Text>
                            <Text style={{ fontWeight: 600, fontSize: "8px", color: colors.neutral[600], fontFamily: 'Inter' }}>
                                {item.operator + "/" + item.operator_iata}
                            </Text>
                        </View>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[800], fontWeight: 600, fontFamily: 'Inter', }}>
                            {item.from_.city + " (" + item.from_.code_iata + "/" + item.from_.code_icao + ")"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.scheduled_in, "yyyy-MM-dd") === "0001-01-01"
                                ? "-"
                                : formatInTimeZone(item.scheduled_in_tz, item.to_tz, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {format(item.scheduled_in, "yyyy-MM-dd") === "0001-01-01"
                                ? ""
                                : formatInTimeZone(item.scheduled_in_tz, item.to_tz, "HH:mm", { locale: enGB }) +
                                " " +
                                item.to_tz_abbr
                            }
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.actual_out, "yyyy-MM-dd") === "0001-01-01")
                                    ? "-"
                                    : formatInTimeZone(item.actual_out_tz, item.from_.timezone, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.actual_out, "yyyy-MM-dd") === "0001-01-01")
                                    ? ""
                                    : formatInTimeZone(item.actual_out_tz, item.from_.timezone, "HH:mm", { locale: enGB }) + " " + item.from_.timezone_abbr
                            }
                        </Text>
                        {
                            formatDelay(item.departure_delay) && format(item.actual_out, "yyyy-MM-dd") !== "0001-01-01" ?
                                <PillsPdf
                                    label={formatDelay(item.departure_delay)!}
                                    color={departuresDelayColor}
                                    size={'sm'}
                                    outline={false}
                                    emphasis={false}
                                /> :
                                format(item.actual_out, "yyyy-MM-dd") !== "0001-01-01" ?
                                    <PillsPdf
                                        label={"On time"}
                                        color={"green"}
                                        size={'sm'}
                                        outline={false}
                                        emphasis={false}
                                    /> : <></>
                        }
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.actual_in, "yyyy-MM-dd") === "0001-01-01" || item.status.cancelled) ? "-" :
                                    formatInTimeZone(item.actual_in_tz, item.to_tz, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                (format(item.actual_in, "yyyy-MM-dd") === "0001-01-01" || item.status.cancelled) ? "" :
                                    formatInTimeZone(item.actual_in_tz, item.to_tz, "HH:mm", { locale: enGB }) +
                                    " " +
                                    item.to_tz_abbr
                            }
                        </Text>
                        {
                            formatDelay(item.arrival_delay) && format(item.actual_in, "yyyy-MM-dd") !== "0001-01-01" ?
                                <PillsPdf
                                    label={formatDelay(item.arrival_delay)!}
                                    color={arrivalsDelayColor}
                                    size={'sm'}
                                    outline={false}
                                    emphasis={false}
                                /> :
                                format(item.actual_in, "yyyy-MM-dd") !== "0001-01-01" ?
                                    <PillsPdf
                                        label={"On time"}
                                        color={"green"}
                                        size={'sm'}
                                        outline={false}
                                        emphasis={false}
                                    /> : <></>
                        }
                    </TableCell>
                </View>
            )
        })
    }

    const arrivalsAirportChunks = chunkArray(arrivalsBody, 13);

    if (props.report.flight_info.departure_airport_weather.hourly !== undefined) {
        props.report.flight_info.departure_airport_weather.hourly.filter(item => item.weather !== null && item.weather.length > 0).forEach((item, index) => {
            departuresWeatherBody.push(
                <View key={index} style={{ flexDirection: "row" }}>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {new Date(item.day_time).toISOString() === "0001-01-01T00:00:00.000Z"
                                ? "-"
                                : formatInTimeZone(item.day_time, props.report.flight_info.flight.departure.airport.timezone!, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {new Date(item.day_time).toISOString() === "0001-01-01T00:00:00.000Z"
                                ? "-"
                                : formatInTimeZone(item.day_time, props.report.flight_info.flight.departure.airport.timezone!, "HH:mm", { locale: enGB })
                            }
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "8px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.temp + " °C"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.dew_point + " °C"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.humidity + "%"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {degreesToDirection(item.wind_deg)}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.wind_speed + " Kts"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.wind_gust + " Kts"}
                        </Text>
                    </TableCell>
                    {/* <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.pressure + " hPa"}
                        </Text>
                    </TableCell> */}
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {cloudsLabel(item.clouds)}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                item.weather[0].main === cloudsLabel(item.clouds) ?
                                    "No significant weather" : item.weather[0].main
                            }
                        </Text>
                    </TableCell>
                </View>
            )
        })
    }

    const departuresHourlyChunks = chunkArray(departuresWeatherBody, 18);

    if (props.report.flight_info.arrival_airport_weather.hourly !== undefined) {
        props.report.flight_info.arrival_airport_weather.hourly.filter(item => item.weather !== null && item.weather.length > 0).forEach((item, index) => {
            arrivalsWeatherBody.push(
                <View key={index} style={{ flexDirection: "row" }}>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {new Date(item.day_time).toISOString() === "0001-01-01T00:00:00.000Z"
                                ? "-"
                                : formatInTimeZone(item.day_time, props.report.flight_info.flight.arrival.airport.timezone!, "dd MMM yyyy", { locale: enGB })
                            }
                        </Text>
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {new Date(item.day_time).toISOString() === "0001-01-01T00:00:00.000Z"
                                ? "-"
                                : formatInTimeZone(item.day_time, props.report.flight_info.flight.arrival.airport.timezone!, "HH:mm", { locale: enGB })
                            }
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "8px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.temp + " °C"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.dew_point + " °C"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.humidity + "%"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {degreesToDirection(item.wind_deg)}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.wind_speed + " Kts"}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.wind_gust + " Kts"}
                        </Text>
                    </TableCell>
                    {/* <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {item.pressure + " hPa"}
                        </Text>
                    </TableCell> */}
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {cloudsLabel(item.clouds)}
                        </Text>
                    </TableCell>
                    <TableCell width="100%">
                        <Text style={{ fontSize: "7px", color: colors.neutral[600], fontFamily: 'Inter', fontWeight: 600 }}>
                            {
                                item.weather[0].main === cloudsLabel(item.clouds) ?
                                    "No significant weather" : item.weather[0].main
                            }
                        </Text>
                    </TableCell>
                </View>
            )
        })
    }

    const arrivalHourlyChunks = chunkArray(arrivalsWeatherBody, 18);

    return (
        <Document>
            <Page size={[720, 842]} style={{ backgroundColor: colors.pas[700], display: "flex", flexDirection: "column", paddingVertical: 30, paddingHorizontal: 20, height: "100%", gap: 100 }}>
                <Image src="/logo_pas_white.png" style={{ height: "75px", width: "150px" }} />
                <View style={{ display: "flex", flexDirection: "column", gap: 16, justifyContent: "flex-start", alignItems: "flex-start" }}>
                    <Text style={{ fontSize: "60px", color: colors.white, padding: 8, borderBottomWidth: 1, borderBottomColor: colors.pas[500], width: "100%", fontFamily: 'Inter', fontWeight: 400 }}>FLIGHT</Text>
                    <View style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", padding: 8, borderBottomWidth: 1, borderBottomColor: colors.pas[500], width: "100%" }}>
                        <Text style={{ fontSize: "60px", color: colors.white, fontFamily: 'Inter', fontWeight: 400 }}>REPORT</Text>
                        <Text style={{ fontSize: "14px", color: colors.white, fontWeight: 600, fontFamily: 'Inter' }}>{"#" + props.report.flight_info.flight.ident_iata}</Text>
                    </View>
                    <View style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 30 }}>
                        <Text style={{ fontSize: "10px", fontWeight: 400, color: colors.white, fontFamily: 'Inter' }}>
                            Compagnia
                        </Text>
                        <Text style={{ fontSize: "16px", fontWeight: 600, color: colors.white, fontFamily: 'Inter' }}>
                            {props.report.flight_info.flight.airline_name}
                        </Text>
                    </View>
                    <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around', alignContent: "flex-start", gap: 8, marginTop: 30 }}>
                        <View style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Text style={{ fontSize: "26px", fontWeight: 600, color: colors.white, fontFamily: 'Inter' }}>{props.report.flight_info.flight.departure.airport.code_iata}</Text>
                            <Text style={{ fontSize: "12px", fontWeight: 400, color: colors.white, fontFamily: 'Inter' }}>{props.report.flight_info.flight.departure.airport.city}</Text>
                        </View>
                        <Svg
                            width="100px"
                            height="24px"
                            viewBox="0 0 201.000000 46.000000"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <G
                                transform="translate(0.000000,46.000000) scale(0.100000,-0.100000)"
                                fill={colors.pas[500]}
                                stroke="none"
                            >
                                <Path d="M923 358 c3 -13 9 -40 12 -60 7 -37 6 -38 -22 -38 -16 0 -34 5 -41 12 -9 9 -12 0 -12 -42 0 -50 1 -52 18 -37 11 10 26 14 41 10 24 -6 24 -12 5 -87 -5 -23 -3 -26 20 -26 22 0 31 9 53 55 l27 55 52 0 c54 0 79 15 68 43 -6 18 -35 27 -86 27 -34 0 -38 4 -60 48 -26 53 -35 62 -62 62 -14 0 -17 -5 -13 -22z" />
                                <Path d="M84 265 c-34 -52 22 -113 73 -79 13 8 23 21 23 29 0 9 10 16 23 16 19 1 20 2 3 6 -11 3 -28 16 -39 29 -24 32 -62 31 -83 -1z" />
                                <Path d="M1834 265 c-9 -14 -24 -25 -33 -26 -11 0 -10 -3 6 -9 12 -5 24 -18 27 -28 16 -49 96 -26 96 27 0 57 -66 82 -96 36z" />
                                <Path d="M258 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M318 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M378 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M508 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M568 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M628 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M688 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M748 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M808 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1178 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1308 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1368 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1428 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1488 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1548 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1608 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                <Path d="M1738 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                            </G>
                        </Svg>
                        <View style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Text style={{ fontSize: "26px", fontWeight: 600, color: colors.white, fontFamily: 'Inter' }}>{props.report.flight_info.flight.arrival.airport.code_iata}</Text>
                            <Text style={{ fontSize: "12px", fontWeight: 400, color: colors.white, fontFamily: 'Inter' }}>{props.report.flight_info.flight.arrival.airport.city}</Text>
                        </View>
                    </View>
                </View>
            </Page>
            <Page size={[720, 842]} style={{ display: 'flex', flexDirection: 'column', paddingVertical: 30, paddingHorizontal: 20, gap: 16 }}>
                <View style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <View style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                        <Svg viewBox="0 0 87.5 87.5" width={24} height={24}>
                            <Path d="M28.59,67.42c0-5.29-2.91-10.14-7.57-12.62-4.66-2.48-10.32-2.2-14.7.75C1.94,58.5-.46,63.63.07,68.88c.54,5.26,3.94,9.79,8.83,11.78l4.09,6.14c.29.44.77.71,1.3.7s1.01-.27,1.3-.7l4.09-6.14c2.63-1.08,4.88-2.91,6.46-5.27,1.59-2.36,2.44-5.13,2.45-7.97h0ZM14.3,74.45c-2.85,0-5.41-1.71-6.5-4.34-1.09-2.63-.49-5.66,1.52-7.67,2.01-2.01,5.04-2.61,7.67-1.52,2.62,1.09,4.34,3.66,4.34,6.5,0,3.88-3.15,7.02-7.02,7.02Z" fill={'#001C29'} />
                            <Path d="M81.34,20.45l-7.39.23L64.63.89c-.27-.54-.82-.89-1.42-.89h-4.92c-.48,0-.93.22-1.23.6-.29.38-.4.87-.29,1.34l4.72,19.12-14.59.5-4.05-5.06c-.3-.37-.75-.59-1.22-.59h-4.55c-.54,0-1.04.29-1.32.75s-.32,1.03-.08,1.52l4.2,8.39-4.2,8.39c-.23.49-.2,1.06.08,1.52.29.46.78.74,1.32.75h4.55c.47,0,.92-.22,1.22-.59l4.05-5.06,14.59.5-4.72,19.13c-.11.46,0,.96.29,1.34.3.38.75.6,1.23.6h4.94c.6,0,1.14-.35,1.41-.89l9.33-19.81,7.44.25c1.62,0,3.18-.64,4.33-1.8,1.15-1.15,1.79-2.71,1.78-4.34,0-1.62-.66-3.18-1.81-4.32-1.16-1.14-2.72-1.78-4.34-1.77h0Z" fill={'#001C29'} />
                            <Path d="M36.72,45.31h-14.06c-2.28,0-4.47-.9-6.09-2.51-1.61-1.61-2.52-3.8-2.52-6.08s.91-4.47,2.52-6.08c1.62-1.61,3.8-2.52,6.09-2.51h8.59c.86,0,1.56-.7,1.56-1.56s-.7-1.56-1.56-1.56h-8.59c-6.47,0-11.72,5.25-11.72,11.72s5.25,11.72,11.72,11.72h14.06c6.42,0,12.35,3.43,15.56,8.98,3.21,5.56,3.21,12.41,0,17.97-3.21,5.56-9.14,8.98-15.56,8.98h-14.91c-.86,0-1.56.7-1.56,1.56s.7,1.56,1.56,1.56h14.91c5.6,0,10.97-2.21,14.93-6.17,3.96-3.96,6.18-9.32,6.18-14.92s-2.22-10.97-6.18-14.92-9.33-6.18-14.93-6.17h0Z" fill={'#001C29'} />
                        </Svg>
                        <Text style={{ color: colors.pas[800], fontSize: "20px", fontWeight: 700, fontFamily: 'Inter' }}>
                            Flight information
                        </Text>
                    </View>
                    <View style={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center', width: '100%', height: "120px" }}>
                        <View style={{ width: '50%', display: 'flex', flexDirection: 'column', gap: 16, padding: 12, backgroundColor: colors.pas[200], borderRadius: 20, height: "80%" }}>
                            <Text style={{ fontSize: "8px", fontWeight: 700, fontFamily: 'Inter', color: colors.pas[800], lineHeight: "24px" }}>
                                Flight route
                            </Text>
                            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around', width: '100%' }}>
                                <View style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <Text style={{ fontSize: "10px", fontWeight: 600, color: colors.pas[700], fontFamily: 'Inter' }}>{props.report.flight_info.flight.departure.airport.code_iata}</Text>
                                    <Text style={{ fontSize: "8px", lineHeight: "12px", fontWeight: 400, color: colors.pas[700], fontFamily: 'Inter' }}>{props.report.flight_info.flight.departure.airport.city}</Text>
                                </View>
                                <Svg
                                    width="100px"
                                    height="24px"
                                    viewBox="0 0 201.000000 46.000000"
                                    preserveAspectRatio="xMidYMid meet"
                                >
                                    <G
                                        transform="translate(0.000000,46.000000) scale(0.100000,-0.100000)"
                                        fill={colors.pas[500]}
                                        stroke="none"
                                    >
                                        <Path d="M923 358 c3 -13 9 -40 12 -60 7 -37 6 -38 -22 -38 -16 0 -34 5 -41 12 -9 9 -12 0 -12 -42 0 -50 1 -52 18 -37 11 10 26 14 41 10 24 -6 24 -12 5 -87 -5 -23 -3 -26 20 -26 22 0 31 9 53 55 l27 55 52 0 c54 0 79 15 68 43 -6 18 -35 27 -86 27 -34 0 -38 4 -60 48 -26 53 -35 62 -62 62 -14 0 -17 -5 -13 -22z" />
                                        <Path d="M84 265 c-34 -52 22 -113 73 -79 13 8 23 21 23 29 0 9 10 16 23 16 19 1 20 2 3 6 -11 3 -28 16 -39 29 -24 32 -62 31 -83 -1z" />
                                        <Path d="M1834 265 c-9 -14 -24 -25 -33 -26 -11 0 -10 -3 6 -9 12 -5 24 -18 27 -28 16 -49 96 -26 96 27 0 57 -66 82 -96 36z" />
                                        <Path d="M258 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M318 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M378 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M508 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M568 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M628 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M688 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M748 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M808 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1178 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1308 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1368 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1428 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1488 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1548 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1608 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                        <Path d="M1738 233 c7 -3 16 -2 19 1 4 3 -2 6 -13 5 -11 0 -14 -3 -6 -6z" />
                                    </G>
                                </Svg>
                                <View style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <Text style={{ fontSize: "10px", fontWeight: 600, color: colors.pas[700], fontFamily: 'Inter' }}>{props.report.flight_info.flight.arrival.airport.code_iata}</Text>
                                    <Text style={{ fontSize: "8px", lineHeight: "14px", fontWeight: 400, color: colors.pas[700], fontFamily: 'Inter' }}>{props.report.flight_info.flight.arrival.airport.city}</Text>
                                </View>
                            </View>
                        </View>
                        <View style={{ width: '35%', display: 'flex', flexDirection: 'column', gap: 16, padding: 12, backgroundColor: colors.pas[200], borderRadius: 20, height: "80%" }}>
                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.pas[800], lineHeight: "24px", fontFamily: 'Inter' }}>Flight info</Text>
                            <View style={{ display: "flex", alignItems: "flex-start", flexDirection: "column", gap: 4 }}>
                                <Text style={{ fontSize: "10px", fontWeight: 600, color: colors.pas[700], fontFamily: 'Inter' }}>{props.report.flight_info.flight.ident_iata}</Text>
                                <Text style={{ fontSize: "8px", fontWeight: 400, color: colors.pas[700], fontFamily: 'Inter' }}>{props.report.flight_info.flight.airline_name}</Text>
                                <Text style={{ fontSize: "8px", fontWeight: 400, color: colors.pas[700], fontFamily: 'Inter' }}>{props.report.flight_info.flight.operated_by}</Text>
                            </View>
                        </View>
                        <View style={{ width: '45%', display: 'flex', flexDirection: 'column', gap: 16, padding: 12, backgroundColor: colors.pas[200], borderRadius: 20, height: "80%" }}>
                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.pas[800], lineHeight: "24px", fontFamily: 'Inter' }}>Aircraft info</Text>
                            <View style={{ display: "flex", alignItems: "flex-start", flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontSize: "10px", fontWeight: 600, color: colors.pas[700], fontFamily: 'Inter' }}>
                                    {
                                        props.report.flight_info.flight.aircraft_name.length! > 25
                                            ? props.report.flight_info.flight.aircraft_name.substring(0, 25).concat("...")
                                            : props.report.flight_info.flight.aircraft_name
                                    }
                                </Text>
                                <Text style={{ fontSize: "8px", fontWeight: 400, color: colors.pas[700], fontFamily: 'Inter' }}>{"Tail number: " + props.report.flight_info.flight.arrival?.tail_number?.tail_number}</Text>
                            </View>
                        </View>
                        <View style={{ width: '20%', display: 'flex', flexDirection: 'column', gap: 16, padding: 12, backgroundColor: colors.pas[200], borderRadius: 20, height: "80%" }}>
                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.pas[800], lineHeight: "24px", fontFamily: 'Inter' }}>Status</Text>
                            <View style={{ display: "flex", alignItems: "flex-start", flexDirection: "column", gap: 4 }}>
                                {
                                    props.report.flight_info.flight.history.diverted ?
                                        <PillsPdf
                                            label={"Diverted"}
                                            color={"red"}
                                            size={'md'}
                                            outline={false}
                                            emphasis={true}
                                        /> :
                                        props.report.flight_info.flight.history.cancelled ?
                                            <PillsPdf
                                                label={"Canceled"}
                                                color={"red"}
                                                size={'md'}
                                                outline={false}
                                                emphasis={true}
                                            /> :
                                            props.report.flight_info.flight.history.blocked ?
                                                <PillsPdf
                                                    label={"Blocked"}
                                                    color={"red"}
                                                    size={'md'}
                                                    outline={false}
                                                    emphasis={true}
                                                /> :
                                                formatDelay(props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay!)?.includes("early") ?
                                                    <PillsPdf
                                                        label={"Landend"}
                                                        color={"green"}
                                                        size={'md'}
                                                        outline={false}
                                                        emphasis={true}
                                                    /> :
                                                    <View style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                                                        <PillsPdf
                                                            label={"Delayed"}
                                                            color={"orange"}
                                                            size={'md'}
                                                            outline={false}
                                                            emphasis={true}
                                                        />
                                                        <Text style={{ fontSize: "8px", fontWeight: 400, color: colors.pas[700], fontFamily: 'Inter' }}>
                                                            {
                                                                (props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay === "" || props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay === "0s") ?
                                                                    "-" : formatDelay(props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay!)
                                                            }
                                                        </Text>
                                                    </View>
                                }
                            </View>
                        </View>
                    </View>
                </View>
                <View style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <Text style={{ color: colors.pas[800], fontSize: "20px", fontWeight: 700, fontFamily: 'Inter' }}>
                        Flight history
                    </Text>
                    <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                        <Text
                            wrap={false}
                            style={{
                                fontWeight: 800,
                                fontSize: "16px",
                                color: colors.pas[800],
                                padding: 4,
                                borderBottomWidth: 2,
                                borderBottomColor: colors.pas[500],
                                fontFamily: 'Inter',
                                flexShrink: 0,
                            }}>
                            Departure
                        </Text>
                        <Text style={{
                            padding: 4,
                            borderBottomWidth: 2,
                            borderBottomColor: colors.pas[200],
                            flexGrow: 1
                        }}></Text>
                    </View>
                    <View style={{ display: "flex", width: "100%", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 20 }}>
                        <View style={{
                            display: "flex",
                            width: "100%",
                            backgroundColor: colors.pas[200],
                            borderRadius: 20,
                            flexDirection: "column",
                            padding: 16,
                            height: "200px",
                            justifyContent: "flex-start",
                            gap: 20
                        }}>
                            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                <Svg width={14} height={14} fill={colors.pas[800]} viewBox="0 0 16 16" >
                                    <Path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                                    <Path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                                </Svg>
                                <Text style={{ fontSize: "12px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                    Departure flight information
                                </Text>
                            </View>
                            <View style={{ flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Departure airport</Text>
                                <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                    {`${props.report.flight_info.flight.departure.airport.name} (${props.report.flight_info.flight.departure.airport.code_iata})`}
                                </Text>
                            </View>
                            <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Date</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {
                                            props.report && format(props.report?.flight_info.flight.departure.flight_gate_times.scheduled_out_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                formatInTimeZone(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out_tz, props.report.flight_info.flight.departure.airport.timezone, "dd/MM/yyyy") :
                                                "-"
                                        }
                                    </Text>
                                </View>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Aircraft</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report?.flight_info.flight.aircraft_name}
                                    </Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter', }}>Terminal</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report.flight_info.flight.departure.terminal_and_gate.terminal_origin || "-"}
                                    </Text>
                                </View>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Gate</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report.flight_info.flight.departure.terminal_and_gate.gate_origin || "-"}
                                    </Text>
                                </View>
                            </View>
                        </View>
                        <View style={{
                            width: "100%",
                            backgroundColor: colors.pas[200],
                            borderRadius: 20,
                            flexDirection: "column",
                            padding: 16,
                            height: "200px",
                            justifyContent: "flex-start",
                            gap: 20
                        }}>

                            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                <Svg width={14} height={14} viewBox="0 0 24 24" fill={colors.pas[200]}>
                                    <Path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke={colors.pas[800]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </Svg>
                                <Text style={{ fontSize: "12px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                    Departure times
                                </Text>
                            </View>
                            <View style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontFamily: "Inter", fontWeight: 800, fontSize: "10px", color: colors.pas[800] }}>
                                    Flight gate times
                                </Text>
                                <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Scheduled out</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out_tz, props.report.flight_info.flight.departure.airport.timezone, "HH:mm") + " " + props.report.flight_info.flight.departure.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Actual out</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report?.flight_info.flight.departure.flight_gate_times.actual_out_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report?.flight_info.flight.departure.flight_gate_times.actual_out_tz, props.report.flight_info.flight.departure.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.departure.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            <View style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontFamily: "Inter", fontWeight: 800, fontSize: "10px", color: colors.pas[800] }}>
                                    Flight runway times
                                </Text>
                                <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Scheduled off</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report?.flight_info.flight.departure.flight_runway_times.scheduled_off_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report?.flight_info.flight.departure.flight_runway_times.scheduled_off_tz, props.report.flight_info.flight.departure.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.departure.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Actual off</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report?.flight_info.flight.departure.flight_runway_times.actual_off_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report?.flight_info.flight.departure.flight_runway_times.actual_off_tz, props.report.flight_info.flight.departure.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.departure.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Departure delay</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report.flight_info.flight.departure.flight_gate_times.departure_delay === "" || props.report.flight_info.flight.departure.flight_gate_times.departure_delay === "0s" ?
                                            "-" :
                                            formatDelay(props.report.flight_info.flight.departure.flight_gate_times.departure_delay!)
                                        }
                                    </Text>
                                </View>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Runway delay</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {
                                            props.report.flight_info.flight.departure.flight_runway_times.runway_delay === "" || props.report.flight_info.flight.departure.flight_runway_times.runway_delay === "0s" ?
                                                "-" :
                                                formatDelay(props.report.flight_info.flight.departure.flight_runway_times.runway_delay!)
                                        }
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>
                    <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                        <Text
                            wrap={false}
                            style={{
                                fontWeight: 800,
                                fontSize: "16px",
                                color: colors.pas[800],
                                padding: 4,
                                borderBottomWidth: 2,
                                borderBottomColor: colors.pas[500],
                                fontFamily: 'Inter',
                                flexShrink: 0,
                            }}>
                            Arrival
                        </Text>
                        <Text style={{
                            padding: 4,
                            borderBottomWidth: 2,
                            borderBottomColor: colors.pas[200],
                            flexGrow: 1
                        }}></Text>
                    </View>
                    <View style={{ width: "100%", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 20 }}>
                        <View style={{
                            width: "100%",
                            backgroundColor: colors.pas[200],
                            borderRadius: 20,
                            flexDirection: "column",
                            padding: 16,
                            height: "250px",
                            justifyContent: "flex-start",
                            gap: 20
                        }}>
                            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                <Svg width={14} height={14} fill={colors.pas[800]} viewBox="0 0 16 16">
                                    <Path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                                    <Path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                                </Svg>
                                <Text style={{ fontSize: "12px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                    Arrival flight information
                                </Text>
                            </View>
                            <View style={{ flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Arrival airport</Text>
                                <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                    {props.report?.flight_info.flight.arrival.airport.name + " (" + props.report?.flight_info.flight.arrival.airport.code_iata + ")"}
                                </Text>
                            </View>
                            {
                                props.report.flight_info.flight.history.diverted &&
                                <View style={{ flexDirection: "column", gap: 6 }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>
                                        Diverted to
                                    </Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report?.flight_info.diverted_flight.arrival.airport.name + " (" + props.report?.flight_info.diverted_flight.arrival.airport.code_iata + ")"}
                                    </Text>
                                </View>
                            }
                            <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Date</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {
                                            props.report && format(props.report?.flight_info.flight.arrival.flight_gate_times.scheduled_in_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                format(props.report?.flight_info.flight.arrival.flight_gate_times.scheduled_in_tz, "dd/MM/yyyy") :
                                                "-"
                                        }
                                    </Text>
                                </View>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Aircraft</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report?.flight_info.flight.aircraft_name}
                                    </Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Terminal</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report?.flight_info.flight.arrival.terminal_and_gate.terminal_destination === "" ? "-" : props.report?.flight_info.flight.arrival.terminal_and_gate.terminal_destination}
                                    </Text>
                                </View>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Gate</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report?.flight_info.flight.arrival.terminal_and_gate.gate_destination !== "" ? props.report?.flight_info.flight.arrival.terminal_and_gate.gate_destination : "-"}
                                    </Text>
                                </View>
                            </View>
                        </View>
                        <View style={{
                            width: "100%",
                            backgroundColor: colors.pas[200],
                            borderRadius: 20,
                            flexDirection: "column",
                            padding: 16,
                            height: "250px",
                            justifyContent: "flex-start",
                            gap: 20
                        }}>
                            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                <Svg width={14} height={14} viewBox="0 0 24 24" fill={colors.pas[200]}>
                                    <Path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke={colors.pas[800]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </Svg>
                                <Text style={{ fontSize: "12px", fontWeight: 700, color: colors.pas[800], fontFamily: 'Inter' }}>
                                    Arrival times
                                </Text>
                            </View>
                            <View style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontFamily: "Inter", fontWeight: 800, fontSize: "10px", color: colors.pas[800] }}>
                                    Flight runway times
                                </Text>
                                <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Scheduled on</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report?.flight_info.flight.arrival.flight_runway_times.scheduled_on_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report?.flight_info.flight.arrival.flight_runway_times.scheduled_on_tz, props.report?.flight_info.flight.arrival.airport.timezone, "HH:mm") + " " + props.report.flight_info.flight.arrival.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Actual on</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report?.flight_info.flight.arrival.flight_runway_times.actual_on_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report?.flight_info.flight.arrival.flight_runway_times.actual_on_tz, props.report?.flight_info.flight.arrival.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.arrival.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            <View style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontFamily: "Inter", fontWeight: 800, fontSize: "10px", color: colors.pas[800] }}>
                                    Flight gate times
                                </Text>
                                <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Scheduled in</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report?.flight_info.flight.arrival.flight_gate_times.scheduled_in_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report?.flight_info.flight.arrival.flight_gate_times.scheduled_in_tz, props.report?.flight_info.flight.arrival.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.arrival.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                    <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                        <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}> Actual in</Text>
                                        <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                            {
                                                props.report && format(props.report?.flight_info.flight.arrival.flight_gate_times.actual_in_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                                    formatInTimeZone(props.report?.flight_info.flight.arrival.flight_gate_times.actual_in_tz, props.report?.flight_info.flight.arrival.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.arrival.airport.timezone_abbr :
                                                    "-"
                                            }
                                        </Text>
                                    </View>
                                </View>
                            </View>
                            <View style={{ flexDirection: "row", justifyContent: "flex-start", gap: 16 }}>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Runway delay</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {props.report.flight_info.flight.arrival.flight_runway_times.runway_delay === "" ?
                                            "-" :
                                            formatDelay(props.report.flight_info.flight.arrival.flight_runway_times.runway_delay!)
                                        }
                                    </Text>
                                </View>
                                <View style={{ flexDirection: "column", gap: 6, width: "50%" }}>
                                    <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Arrival delay</Text>
                                    <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                        {
                                            (props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay === "" || props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay === "0s") ?
                                                "-" : formatDelay(props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay!)
                                        }
                                    </Text>
                                </View>
                            </View>
                            <View style={{ flexDirection: "column", gap: 6 }}>
                                <Text style={{ fontSize: "8px", fontWeight: 600, color: colors.pas[800], fontFamily: 'Inter' }}>Flight time</Text>
                                <Text style={{ fontSize: "10px", fontFamily: 'Inter', fontWeight: 400 }}>
                                    {formatFlightTime(props.report.flight_info.flight.arrival.flight_time)}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>
                <Footer sources={sourcesApi} />
            </Page>
            {
                rotationChunks.map((chunk, index) => (
                    <Page size={[720, 842]} style={{ display: 'flex', flexDirection: 'column', paddingVertical: 30, paddingHorizontal: 20, gap: 16 }}>
                        {
                            index === 0 &&
                            <View style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                <Svg width={20} height={20} viewBox="0 0 24 24" fill={colors.pas[200]}>
                                    <Path d="M17.62 9.62H12.37C11.96 9.62 11.62 9.28 11.62 8.87C11.62 8.46 11.96 8.12 12.37 8.12H17.62C18.03 8.12 18.37 8.46 18.37 8.87C18.37 9.28 18.04 9.62 17.62 9.62Z" fill={colors.pas[800]} />
                                    <Path d="M7.12 10.3799C6.93 10.3799 6.74 10.3099 6.59 10.1599L5.84 9.40995C5.55 9.11995 5.55 8.63995 5.84 8.34995C6.13 8.05995 6.61 8.05995 6.9 8.34995L7.12 8.56995L8.84 6.84995C9.13 6.55995 9.61 6.55995 9.9 6.84995C10.19 7.13995 10.19 7.61995 9.9 7.90995L7.65 10.1599C7.51 10.2999 7.32 10.3799 7.12 10.3799Z" fill={colors.pas[800]} />
                                    <Path d="M17.62 16.62H12.37C11.96 16.62 11.62 16.28 11.62 15.87C11.62 15.46 11.96 15.12 12.37 15.12H17.62C18.03 15.12 18.37 15.46 18.37 15.87C18.37 16.28 18.04 16.62 17.62 16.62Z" fill={colors.pas[800]} />
                                    <Path d="M7.12 17.3799C6.93 17.3799 6.74 17.3099 6.59 17.1599L5.84 16.4099C5.55 16.1199 5.55 15.6399 5.84 15.3499C6.13 15.0599 6.61 15.0599 6.9 15.3499L7.12 15.5699L8.84 13.8499C9.13 13.5599 9.61 13.5599 9.9 13.8499C10.19 14.1399 10.19 14.6199 9.9 14.9099L7.65 17.1599C7.51 17.2999 7.32 17.3799 7.12 17.3799Z" fill={colors.pas[800]} />
                                    <Path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" fill={colors.pas[800]} />
                                </Svg>
                                <Text style={{ color: colors.pas[800], fontSize: "16px", fontWeight: 700, fontFamily: 'Inter' }}>
                                    {"Aircraft Flight History"}
                                </Text>
                                <Text style={{ color: colors.pas[800], fontSize: "12px", fontWeight: 400, fontFamily: 'Inter' }}>
                                    {"Tail number: " + (props.report.flight_info.flight.arrival.tail_number.tail_number ? props.report.flight_info.flight.arrival.tail_number.tail_number : "-")}
                                </Text>
                            </View>
                        }
                        <View style={{
                            width: "100%",
                            flexDirection: "column",
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            overflow: "hidden"
                        }}>
                            {/* Header Row */}
                            <View style={{ flexDirection: "row" }}>
                                <TableHeaderCell width="100%" >DATE/STATUS</TableHeaderCell>
                                <TableHeaderCell width="100%">FLIGHT</TableHeaderCell>
                                <TableHeaderCell width="100%">DEPARTURE</TableHeaderCell>
                                <TableHeaderCell width="100%">ARRIVAL</TableHeaderCell>
                                <TableHeaderCell width="100%">SCHEDULED OUT</TableHeaderCell>
                                <TableHeaderCell width="100%" >ACTUAL OUT</TableHeaderCell>
                                <TableHeaderCell width="100%">SCHEDULED IN</TableHeaderCell>
                                <TableHeaderCell width="100%" >ACTUAL IN</TableHeaderCell>
                            </View>
                            {
                                !rotations || rotations.length === 0 ? (
                                    <View style={{ flexDirection: "row", backgroundColor: "#ffffff", height: 56 }}>
                                        <TableCell width="100%">
                                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.neutral[600], fontFamily: 'Inter' }}>Result not available</Text>
                                        </TableCell>
                                    </View>
                                ) : (
                                    chunk
                                )
                            }
                        </View>
                        <Footer sources={sourcesApi} />
                    </Page>
                ))
            }
            {
                departuresAirportChunks.map((chunk, index) => (
                    <Page size={[720, 842]} style={{ display: 'flex', flexDirection: 'column', paddingVertical: 30, paddingHorizontal: 20, gap: 10 }}>
                        {
                            index === 0 &&
                            <>
                                <View style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                    <Svg style={{ transform: "rotate(50deg)" }} width={20} height={20} fill={colors.pas[800]} viewBox="0 0 16 16">
                                        <Path d="M6.428 1.151C6.708.591 7.213 0 8 0s1.292.592 1.572 1.151C9.861 1.73 10 2.431 10 3v3.691l5.17 2.585a1.5 1.5 0 0 1 .83 1.342V12a.5.5 0 0 1-.582.493l-5.507-.918-.375 2.253 1.318 1.318A.5.5 0 0 1 10.5 16h-5a.5.5 0 0 1-.354-.854l1.319-1.318-.376-2.253-5.507.918A.5.5 0 0 1 0 12v-1.382a1.5 1.5 0 0 1 .83-1.342L6 6.691V3c0-.568.14-1.271.428-1.849" />
                                    </Svg>
                                    <Text style={{ color: colors.pas[800], fontSize: "16px", fontWeight: 700, fontFamily: 'Inter' }}>
                                        Airports Traffic
                                    </Text>
                                </View>
                                <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                                    <Text
                                        wrap={false}
                                        style={{
                                            fontWeight: 800,
                                            fontSize: "16px",
                                            color: colors.pas[800],
                                            padding: 4,
                                            borderBottomWidth: 2,
                                            borderBottomColor: colors.pas[500],
                                            fontFamily: 'Inter',
                                            flexShrink: 0,
                                        }}>
                                        {"Departures - " + props.report.flight_info.flight.departure.airport.city! + " Airport" + " (" + props.report.flight_info.flight.departure.airport.code_iata + ")"}
                                    </Text>
                                    <Text style={{
                                        padding: 4,
                                        borderBottomWidth: 2,
                                        borderBottomColor: colors.pas[200],
                                        flexGrow: 1,
                                    }}>
                                    </Text>
                                </View>
                            </>
                        }
                        <View style={{
                            width: "100%",
                            flexDirection: "column",
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            overflow: "hidden"
                        }}>
                            {/* Header Row */}
                            <View style={{ flexDirection: "row" }}>
                                <TableHeaderCell width="100%" >DATE/STATUS</TableHeaderCell>
                                <TableHeaderCell width="100%">FLIGHT</TableHeaderCell>
                                <TableHeaderCell width="100%">DESTINATION</TableHeaderCell>
                                <TableHeaderCell width="100%">SCHEDULED OUT</TableHeaderCell>
                                <TableHeaderCell width="100%" >ACTUAL OUT</TableHeaderCell>
                                <TableHeaderCell width="100%" >ACTUAL IN</TableHeaderCell>
                            </View>
                            {
                                !departures || departures.length === 0 ? (
                                    <View style={{ flexDirection: "row", backgroundColor: "#ffffff", height: "40px" }}>
                                        <TableCell width="100%">
                                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.neutral[600], fontFamily: 'Inter' }}>Result not available</Text>
                                        </TableCell>
                                    </View>
                                ) : (
                                    chunk
                                )
                            }
                        </View>
                        <Footer sources={sourcesApiDepartures} />
                    </Page>
                ))
            }
            {
                arrivalsAirportChunks.map((chunk, index) => (
                    <Page size={[720, 842]} style={{ display: 'flex', flexDirection: 'column', paddingVertical: 30, paddingHorizontal: 20, gap: 16 }}>
                        {
                            index === 0 &&
                            <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                                <Text
                                    wrap={false}
                                    style={{
                                        fontWeight: 800,
                                        fontSize: "16px",
                                        color: colors.pas[800],
                                        padding: 4,
                                        borderBottomWidth: 2,
                                        borderBottomColor: colors.pas[500],
                                        fontFamily: 'Inter',
                                        flexShrink: 0,
                                    }}>
                                    {"Arrivals - " + props.report.flight_info.flight.arrival.airport.city! + " Airport" + " (" + props.report.flight_info.flight.arrival.airport.code_iata + ")"}
                                </Text>
                                <Text style={{
                                    padding: 4,
                                    borderBottomWidth: 2,
                                    borderBottomColor: colors.pas[200],
                                    flexGrow: 1
                                }}>
                                </Text>
                            </View>
                        }

                        <View style={{
                            width: "100%",
                            flexDirection: "column",
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            overflow: "hidden"
                        }}>
                            <View style={{ flexDirection: "row" }}>
                                <TableHeaderCell width="100%" >DATE/STATUS</TableHeaderCell>
                                <TableHeaderCell width="100%">FLIGHT</TableHeaderCell>
                                <TableHeaderCell width="100%">FROM</TableHeaderCell>
                                <TableHeaderCell width="100%">SCHEDULED IN</TableHeaderCell>
                                <TableHeaderCell width="100%" >ACTUAL OUT</TableHeaderCell>
                                <TableHeaderCell width="100%" >ACTUAL IN</TableHeaderCell>
                            </View>
                            {
                                !arrivals || arrivals.length === 0 ? (
                                    <View style={{ flexDirection: "row", backgroundColor: "#ffffff", height: "40px" }}>
                                        <TableCell width="100%">
                                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.neutral[600], fontFamily: 'Inter' }}>Result not available</Text>
                                        </TableCell>
                                    </View>
                                ) : (
                                    chunk
                                )
                            }
                        </View>
                        <Footer sources={sourcesApiArrivals} />
                    </Page>
                ))
            }
            {
                departuresHourlyChunks.map((chunk, index) => (
                    <Page size={[720, 842]} style={{ display: 'flex', flexDirection: 'column', paddingVertical: 30, paddingHorizontal: 20, gap: 10 }}>
                        {
                            index === 0 &&
                            <>
                                <View style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                    <Svg width={20} height={20} viewBox="0 0 24 24" fill={colors.neutral[200]}>
                                        <G clipPath="url(#clip0_24_1224)">
                                            <Path d="M12 1V3M12 21V23M4.22 4.22L5.64 5.64M18.36 18.36L19.78 19.78M1 12H3M21 12H23M4.22 19.78L5.64 18.36M18.36 5.64L19.78 4.22M17 12C17 14.7614 14.7614 17 12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7C14.7614 7 17 9.23858 17 12Z" stroke={colors.pas[800]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </G>
                                        <Defs>
                                            <clipPath id="clip0_24_1224">
                                                <rect width="24" height="24" fill="white" />
                                            </clipPath>
                                        </Defs>
                                    </Svg>

                                    <Text style={{ color: colors.pas[800], fontSize: "16px", fontWeight: 700, fontFamily: 'Inter' }}>
                                        Weather
                                    </Text>
                                </View>
                                <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                                    <Text
                                        wrap={false}
                                        style={{
                                            fontWeight: 800,
                                            fontSize: "16px",
                                            color: colors.pas[800],
                                            padding: 4,
                                            borderBottomWidth: 2,
                                            borderBottomColor: colors.pas[500],
                                            fontFamily: 'Inter',
                                            flexShrink: 0,
                                        }}>
                                        {"Departure - " + props.report.flight_info.flight.departure.airport.city! + " Airport" + " (" + props.report.flight_info.flight.departure.airport.code_iata + ")"}
                                    </Text>
                                    <Text style={{
                                        padding: 4,
                                        borderBottomWidth: 2,
                                        borderBottomColor: colors.pas[200],
                                        flexGrow: 1
                                    }}>
                                    </Text>
                                </View>
                            </>
                        }
                        <View style={{
                            width: "100%",
                            flexDirection: "column",
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            overflow: "hidden"
                        }}>
                            <View style={{ flexDirection: "row", width: "100%", }}>
                                <TableHeaderCell width="100%">TIME {'(' + props.report.flight_info.flight.departure.airport.timezone_abbr + ')'}</TableHeaderCell>
                                <TableHeaderCell width="100%">TEMP.</TableHeaderCell>
                                <TableHeaderCell width="100%">DW POINT</TableHeaderCell>
                                <TableHeaderCell width="100%">HUMIDITY</TableHeaderCell>
                                <TableHeaderCell width="100%">WIND</TableHeaderCell>
                                <TableHeaderCell width="100%">WIND SPEED</TableHeaderCell>
                                <TableHeaderCell width="100%">WIND GUST</TableHeaderCell>
                                {/* <TableHeaderCell width="100%">PRESS.</TableHeaderCell> */}
                                <TableHeaderCell width="100%">CLOUDS</TableHeaderCell>
                                <TableHeaderCell width="100%">CONDITION</TableHeaderCell>
                            </View>
                            {
                                !departureWeather || departureWeather.length === 0 ? (
                                    <View style={{ flexDirection: "row", backgroundColor: "#ffffff", height: "40px" }}>
                                        <TableCell width="100%">
                                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.neutral[600], fontFamily: 'Inter' }}>Result not available</Text>
                                        </TableCell>
                                    </View>
                                ) : (
                                    chunk
                                )
                            }
                        </View>
                        <Footer sources={sourcesApiMetarDeparture} />
                    </Page>
                ))
            }
            {
                arrivalHourlyChunks.map((chunck, index) => (
                    <Page size={[720, 842]} style={{ display: 'flex', flexDirection: 'column', paddingVertical: 30, paddingHorizontal: 20, gap: 10 }}>
                        {
                            index === 0 &&
                            <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                                <Text
                                    wrap={false}
                                    style={{
                                        fontWeight: 800,
                                        fontSize: "16px",
                                        color: colors.pas[800],
                                        padding: 4,
                                        borderBottomWidth: 2,
                                        borderBottomColor: colors.pas[500],
                                        fontFamily: 'Inter',
                                        flexShrink: 0,
                                    }}>
                                    {"Arrival - " + props.report.flight_info.flight.arrival.airport.city! + " Airport" + " (" + props.report.flight_info.flight.arrival.airport.code_iata + ")"}
                                </Text>
                                <Text style={{
                                    padding: 4,
                                    borderBottomWidth: 2,
                                    borderBottomColor: colors.pas[200],
                                    flexGrow: 1
                                }}>
                                </Text>
                            </View>
                        }
                        <View style={{
                            width: "100%",
                            flexDirection: "column",
                            borderTopLeftRadius: 10,
                            borderTopRightRadius: 10,
                            overflow: "hidden"
                        }}>
                            <View style={{ flexDirection: "row", width: "100%", }}>
                                <TableHeaderCell width="100%">TIME {'(' + props.report.flight_info.flight.arrival.airport.timezone_abbr + ')'}</TableHeaderCell>
                                <TableHeaderCell width="100%">TEMP.</TableHeaderCell>
                                <TableHeaderCell width="100%">DW POINT</TableHeaderCell>
                                <TableHeaderCell width="100%">HUMIDITY</TableHeaderCell>
                                <TableHeaderCell width="100%">WIND</TableHeaderCell>
                                <TableHeaderCell width="100%">WIND SPEED</TableHeaderCell>
                                <TableHeaderCell width="100%">WIND GUST</TableHeaderCell>
                                {/* <TableHeaderCell width="100%">PRESS.</TableHeaderCell> */}
                                <TableHeaderCell width="100%">CLOUDS</TableHeaderCell>
                                <TableHeaderCell width="100%">CONDITION</TableHeaderCell>
                            </View>
                            {
                                !arrivalWeather || arrivalWeather.length === 0 ? (
                                    <View style={{ flexDirection: "row", backgroundColor: "#ffffff", height: "40px" }}>
                                        <TableCell width="100%">
                                            <Text style={{ fontSize: "8px", fontWeight: 700, color: colors.neutral[600], fontFamily: 'Inter' }}>Result not available</Text>
                                        </TableCell>
                                    </View>
                                ) : (
                                    chunck
                                )
                            }
                        </View>
                        <Footer sources={sourcesApiMetarArrivals} />
                    </Page>
                ))
            }
            {
                props.report.active_notam &&
                notamDepartureChunks.map((chunk, index) => (
                    <Page
                        key={`notam-dep-page-${index}`}
                        size={[720, 842]}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            paddingVertical: 30,
                            paddingHorizontal: 20,
                            gap: 10,
                        }}
                    >
                        {
                            index === 0 &&
                            <>
                                <View style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                    <Text style={{ color: colors.pas[800], fontSize: "16px", fontWeight: 700, fontFamily: 'Inter' }}>
                                        Notam
                                    </Text>
                                </View>
                                <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                                    <Text
                                        wrap={false}
                                        style={{
                                            fontWeight: 800,
                                            fontSize: "16px",
                                            color: colors.pas[800],
                                            padding: 4,
                                            borderBottomWidth: 2,
                                            borderBottomColor: colors.pas[500],
                                            fontFamily: 'Inter',
                                            flexShrink: 0,
                                        }}>
                                        {"Departure - " + props.report.flight_info.flight.departure.airport.city! + " Airport" + " (" + props.report.flight_info.flight.departure.airport.code_iata + ")"}
                                    </Text>
                                    <Text style={{
                                        padding: 4,
                                        borderBottomWidth: 2,
                                        borderBottomColor: colors.pas[200],
                                        flexGrow: 1
                                    }}>
                                    </Text>
                                </View>
                            </>
                        }
                        <View style={{ paddingLeft: 16 }}>
                            {chunk.map((notam: any, idx: number) => (
                                <View
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        gap: 4,
                                        marginBottom: 24,
                                    }}
                                >
                                    <Text style={{
                                        fontSize: 10,
                                        marginRight: 6,
                                        lineHeight: 1.5,
                                        fontFamily: 'Inter',
                                    }}>•</Text>
                                    <View style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                        flex: 1,
                                    }}>
                                        <View style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                                <Text style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: colors.pas[800],
                                                    fontFamily: 'Inter',
                                                }}>
                                                    {notam.startdate.toString() === "2222-02-02T02:02:00Z" ? "N/A " : formatInTimeZone(notam.startdate, props.report.flight_info.flight.arrival.airport.timezone!, 'dd/MM/yyyy HH:mm') + " "}
                                                    -
                                                    {notam.enddate.toString() === "2222-02-02T02:02:00Z" ? " N/A" : " " + formatInTimeZone(notam.enddate, props.report.flight_info.flight.arrival.airport.timezone!, 'dd/MM/yyyy HH:mm')}
                                                </Text>
                                                <View
                                                    style={{
                                                        height: "14px",
                                                        borderRadius: 10,
                                                        paddingHorizontal: 6,
                                                        paddingVertical: 0,
                                                        backgroundColor: colors.brandPrimary[100],
                                                        borderColor: colors.brandPrimary[500],
                                                        display: "flex", justifyContent: "center", alignItems: "center", gap: 4, borderWidth: 1, borderStyle: "solid"
                                                    }}
                                                >
                                                    <Text style={{
                                                        fontSize: '8px',
                                                        fontWeight: 600,
                                                        textAlign: 'center',
                                                        fontFamily: 'Inter',
                                                        color: colors.brandPrimary[500],

                                                    }}>
                                                        {notam.ai_main_category === "" ? 'N/A' : notam.ai_main_category}
                                                    </Text>
                                                </View>
                                            </View>
                                            <Text style={{
                                                fontSize: 10,
                                                fontWeight: 400,
                                                paddingLeft: 12,
                                                fontFamily: 'Inter',
                                            }}>
                                                {notam.ai_interpretation || notam.description || 'N/A'}
                                            </Text>
                                        </View>
                                    </View>
                                </View>
                            ))}
                        </View>
                        <Footer sources={sourcesApiNotam} />
                    </Page>
                ))
            }
            {props.report.active_notam &&
                notamArrivalChunks.map((chunk, index) => (
                    <Page
                        key={`notam-arr-page-${index}`}
                        size={[720, 842]}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            paddingVertical: 30,
                            paddingHorizontal: 20,
                            gap: 10,
                        }}
                    >
                        {
                            index === 0 &&
                            <>
                                <View style={{ width: "100%", display: "flex", flexDirection: "row", gap: 0 }}>
                                    <Text
                                        wrap={false}
                                        style={{
                                            fontWeight: 800,
                                            fontSize: "16px",
                                            color: colors.pas[800],
                                            padding: 4,
                                            borderBottomWidth: 2,
                                            borderBottomColor: colors.pas[500],
                                            fontFamily: 'Inter',
                                            flexShrink: 0,
                                        }}>
                                        {"Arrival - " + props.report.flight_info.flight.arrival.airport.city! + " Airport" + " (" + props.report.flight_info.flight.arrival.airport.code_iata + ")"}
                                    </Text>
                                    <Text style={{
                                        padding: 4,
                                        borderBottomWidth: 2,
                                        borderBottomColor: colors.pas[200],
                                        flexGrow: 1
                                    }}>
                                    </Text>
                                </View>
                            </>
                        }
                        <View style={{ paddingLeft: 16 }}>
                            {chunk.map((notam: any, idx: number) => (
                                <View
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start',
                                        gap: 4,
                                        marginBottom: 24,
                                    }}
                                >
                                    <Text style={{
                                        fontSize: 10,
                                        marginRight: 6,
                                        lineHeight: 1.5,
                                        fontFamily: 'Inter',
                                    }}>•</Text>
                                    <View style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                        flex: 1,
                                    }}>
                                        <View style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                                <Text style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: colors.pas[800],
                                                    fontFamily: 'Inter',
                                                }}>
                                                    {notam.startdate.toString() === "2222-02-02T02:02:00Z" ? "N/A " : formatInTimeZone(notam.startdate, props.report.flight_info.flight.arrival.airport.timezone!, 'dd/MM/yyyy HH:mm') + " "}
                                                    -
                                                    {notam.enddate.toString() === "2222-02-02T02:02:00Z" ? " N/A" : " " + formatInTimeZone(notam.enddate, props.report.flight_info.flight.arrival.airport.timezone!, 'dd/MM/yyyy HH:mm')}
                                                </Text>
                                                <View
                                                    style={{
                                                        height: "14px",
                                                        borderRadius: 10,
                                                        paddingHorizontal: 6,
                                                        paddingVertical: 0,
                                                        backgroundColor: colors.brandPrimary[100],
                                                        borderColor: colors.brandPrimary[500],
                                                        display: "flex", justifyContent: "center", alignItems: "center", gap: 4, borderWidth: 1, borderStyle: "solid"
                                                    }}
                                                >
                                                    <Text style={{
                                                        fontSize: '8px',
                                                        fontWeight: 600,
                                                        textAlign: 'center',
                                                        fontFamily: 'Inter',
                                                        color: colors.brandPrimary[500],

                                                    }}>
                                                        {notam.ai_main_category === "" ? 'N/A' : notam.ai_main_category}
                                                    </Text>
                                                </View>
                                            </View>
                                            <Text style={{
                                                fontSize: 10,
                                                fontWeight: 400,
                                                paddingLeft: 12,
                                                fontFamily: 'Inter',
                                            }}>
                                                {notam.ai_interpretation || notam.description || 'N/A'}
                                            </Text>
                                        </View>
                                    </View>
                                </View>
                            ))}
                        </View>
                        <Footer sources={sourcesApiNotam} />
                    </Page>
                ))}
            <Page size={[720, 842]} style={{ display: 'flex', flexDirection: 'column', paddingVertical: 30, paddingHorizontal: 20, gap: 16 }}>
                <View style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                    <Text style={{ color: colors.pas[800], fontSize: "16px", fontWeight: 700, fontFamily: 'Inter' }}>
                        Final Analysis
                    </Text>
                </View>
                <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: 8 }}>
                    <View style={{ width: '30%' }}>
                        <Text style={{ fontSize: "14px", fontWeight: 600, color: colors.pas[800], fontFamily: "Inter" }}>Flight Date:</Text>
                    </View>
                    <View style={{ width: '70%' }}>
                        <Text style={{ fontSize: "10px", fontWeight: 400, color: colors.pas[800], fontFamily: "Inter" }}>
                            {props.report &&
                                format(new Date(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out_tz), "yyyy-MM-dd") !== "0001-01-01"
                                ? format(new Date(props.report.flight_info.flight.departure.flight_gate_times.scheduled_out_tz), "dd/MM/yyyy")
                                : "-"}
                        </Text>
                    </View>
                </View>
                <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: 8 }}>
                    <View style={{ width: '30%' }}>
                        <Text style={{ fontSize: "14px", fontWeight: 600, color: colors.pas[800], fontFamily: "Inter" }}>Route:</Text>
                    </View>
                    <View style={{ width: '70%', display: 'flex', flexDirection: 'row' }}>
                        <View style={{ width: '50%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Svg style={{ transform: "rotate(50deg)" }} width={14} height={14} fill={colors.pas[800]} viewBox="0 0 16 16">
                                <Path d="M6.428 1.151C6.708.591 7.213 0 8 0s1.292.592 1.572 1.151C9.861 1.73 10 2.431 10 3v3.691l5.17 2.585a1.5 1.5 0 0 1 .83 1.342V12a.5.5 0 0 1-.582.493l-5.507-.918-.375 2.253 1.318 1.318A.5.5 0 0 1 10.5 16h-5a.5.5 0 0 1-.354-.854l1.319-1.318-.376-2.253-5.507.918A.5.5 0 0 1 0 12v-1.382a1.5 1.5 0 0 1 .83-1.342L6 6.691V3c0-.568.14-1.271.428-1.849" />
                            </Svg>
                            <Text style={{ fontSize: "10px", fontWeight: 400, color: colors.pas[800], fontFamily: "Inter" }}>
                                {props.report.flight_info.flight.departure.airport.name}
                            </Text>
                        </View>
                        <View style={{ width: '50%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Svg style={{ transform: "rotate(120deg)" }} width={14} height={14} fill={colors.pas[800]} viewBox="0 0 16 16">
                                <Path d="M6.428 1.151C6.708.591 7.213 0 8 0s1.292.592 1.572 1.151C9.861 1.73 10 2.431 10 3v3.691l5.17 2.585a1.5 1.5 0 0 1 .83 1.342V12a.5.5 0 0 1-.582.493l-5.507-.918-.375 2.253 1.318 1.318A.5.5 0 0 1 10.5 16h-5a.5.5 0 0 1-.354-.854l1.319-1.318-.376-2.253-5.507.918A.5.5 0 0 1 0 12v-1.382a1.5 1.5 0 0 1 .83-1.342L6 6.691V3c0-.568.14-1.271.428-1.849" />
                            </Svg>
                            <Text style={{ fontSize: "10px", fontWeight: 400, color: colors.pas[800], fontFamily: "Inter" }}>
                                {props.report.flight_info.flight.arrival.airport.name}
                            </Text>
                        </View>
                    </View>
                </View>
                <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: 8 }}>
                    <View style={{ width: '30%' }}>
                        <Text style={{ fontSize: "14px", fontWeight: 600, color: colors.pas[800], fontFamily: "Inter" }}>Departure time and arrival time:</Text>
                    </View>
                    <View style={{ width: '70%', display: 'flex', flexDirection: 'row' }}>
                        <View style={{ width: '50%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Svg style={{ transform: "rotate(50deg)" }} width={14} height={14} fill={colors.pas[800]} viewBox="0 0 16 16">
                                <Path d="M6.428 1.151C6.708.591 7.213 0 8 0s1.292.592 1.572 1.151C9.861 1.73 10 2.431 10 3v3.691l5.17 2.585a1.5 1.5 0 0 1 .83 1.342V12a.5.5 0 0 1-.582.493l-5.507-.918-.375 2.253 1.318 1.318A.5.5 0 0 1 10.5 16h-5a.5.5 0 0 1-.354-.854l1.319-1.318-.376-2.253-5.507.918A.5.5 0 0 1 0 12v-1.382a1.5 1.5 0 0 1 .83-1.342L6 6.691V3c0-.568.14-1.271.428-1.849" />
                            </Svg>
                            <Text style={{ fontSize: "10px", fontWeight: 400, color: colors.pas[800], fontFamily: "Inter" }}>
                                {
                                    props.report && format(props.report?.flight_info.flight.departure.flight_gate_times.scheduled_out_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                        formatInTimeZone(props.report?.flight_info.flight.departure.flight_gate_times.scheduled_out_tz, props.report.flight_info.flight.departure.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.departure.airport.timezone_abbr :
                                        "-"
                                }
                            </Text>
                        </View>
                        <View style={{ width: '50%', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Svg style={{ transform: "rotate(120deg)" }} width={14} height={14} fill={colors.pas[800]} viewBox="0 0 16 16">
                                <Path d="M6.428 1.151C6.708.591 7.213 0 8 0s1.292.592 1.572 1.151C9.861 1.73 10 2.431 10 3v3.691l5.17 2.585a1.5 1.5 0 0 1 .83 1.342V12a.5.5 0 0 1-.582.493l-5.507-.918-.375 2.253 1.318 1.318A.5.5 0 0 1 10.5 16h-5a.5.5 0 0 1-.354-.854l1.319-1.318-.376-2.253-5.507.918A.5.5 0 0 1 0 12v-1.382a1.5 1.5 0 0 1 .83-1.342L6 6.691V3c0-.568.14-1.271.428-1.849" />
                            </Svg>
                            <Text style={{ fontSize: "10px", fontWeight: 400, color: colors.pas[800], fontFamily: "Inter" }}>
                                {
                                    props.report && format(props.report?.flight_info.flight.arrival.flight_gate_times.scheduled_in_tz, "yyyy-MM-dd") !== "0001-01-01" ?
                                        formatInTimeZone(props.report?.flight_info.flight.arrival.flight_gate_times.scheduled_in_tz, props.report?.flight_info.flight.arrival.airport.timezone, "HH:mm") + " " + props.report?.flight_info.flight.arrival.airport.timezone_abbr :
                                        "-"
                                }
                            </Text>
                        </View>
                    </View>
                </View>
                {/* <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', marginBottom: 8 }}>
                    <View style={{ width: '30%' }}>
                        <Text style={{ fontSize: "14px", fontWeight: 600, color: colors.pas[800], fontFamily: "Inter" }}>Flight status:</Text>
                    </View>
                     <View style={{ width: '20%' }}>
                        {
                            props.report.flight_info.flight.history.diverted ?
                                <PillsPdf
                                    label={"Diverted"}
                                    color={"red"}
                                    size={'md'}
                                    outline={false}
                                    emphasis={true}
                                /> :
                                props.report.flight_info.flight.history.cancelled ?
                                    <PillsPdf
                                        label={"Canceled"}
                                        color={"red"}
                                        size={'md'}
                                        outline={false}
                                        emphasis={true}
                                    /> :
                                    props.report.flight_info.flight.history.blocked ?
                                        <PillsPdf
                                            label={"Blocked"}
                                            color={"red"}
                                            size={'md'}
                                            outline={false}
                                            emphasis={true}
                                        /> :
                                        formatDelay(props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay!)?.includes("early") ?
                                            <PillsPdf
                                                label={"Landend"}
                                                color={"green"}
                                                size={'md'}
                                                outline={false}
                                                emphasis={true}
                                            /> :
                                            <View style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                                                <PillsPdf
                                                    label={"Delayed"}
                                                    color={"orange"}
                                                    size={'md'}
                                                    outline={false}
                                                    emphasis={true}
                                                />
                                                <Text style={{ fontSize: "8px", fontWeight: 400, color: colors.pas[700], fontFamily: 'Inter' }}>
                                                    {
                                                        (props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay === "" || props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay === "0s") ?
                                                            "-" : formatDelay(props.report.flight_info.flight.arrival.flight_gate_times.arrival_delay!)
                                                    }
                                                </Text>
                                            </View>
                        }
                    </View>
                </View> */}
                <View style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                    <Text style={{ fontSize: "14px", fontWeight: 600, color: colors.pas[800], fontFamily: "Inter" }}>
                        Report Analysis
                    </Text>
                    <View style={{ paddingLeft: 4 }}>
                        {
                            props.report.report ?
                                htmlToPdfElements(props.report.report) :
                                (
                                    <Text style={{ fontSize: "10px", fontWeight: 400, color: colors.pas[800], fontFamily: "Inter" }}>
                                        No analysis provided for this report.
                                    </Text>
                                )
                        }
                    </View>
                </View>
                <Footer sources={sourcesApi} />
            </Page>
        </Document >
    );
};